import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, Users, Globe, Phone } from "lucide-react";

export const WhySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="why"
      data-testid="why-section"
      ref={ref}
      className="min-h-screen bg-[#F5ECD7] py-24 md:py-32"
    >
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        
        {/* Simple Opening */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="max-w-2xl mb-12"
        >
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#7A9E7E] mb-6 block">
            Why We Started
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#2C2C2C] leading-[1.1]">
            Everyone has a story.
            <br />
            <span className="italic">Not everyone has someone to tell it to.</span>
          </h2>
        </motion.div>

        {/* Where we noticed this - Tags */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-wrap gap-3 mb-12"
        >
          <span className="inline-flex items-center gap-2 bg-[#C17A54] text-white px-4 py-2 rounded-full text-sm font-sans">
            <MessageCircle className="w-4 h-4" />
            Reddit threads
          </span>
          <span className="inline-flex items-center gap-2 bg-[#7A9E7E] text-white px-4 py-2 rounded-full text-sm font-sans">
            <Users className="w-4 h-4" />
            Random conversations
          </span>
          <span className="inline-flex items-center gap-2 bg-[#E8A838] text-[#2C2C2C] px-4 py-2 rounded-full text-sm font-sans">
            <Globe className="w-4 h-4" />
            Strangers online
          </span>
          <span className="inline-flex items-center gap-2 bg-[#2C2C2C] text-white px-4 py-2 rounded-full text-sm font-sans">
            <Phone className="w-4 h-4" />
            Quiet DMs
          </span>
        </motion.div>

        {/* The Discovery - What we found */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-3xl mb-16 md:mb-24"
        >
          <p className="font-sans text-xl md:text-2xl text-[#2C2C2C]/70 leading-relaxed mb-6">
            Same pattern everywhere—people talking about <span className="text-[#2C2C2C] font-medium italic">not living</span>, 
            just existing. Going through the motions.
          </p>
          <p className="font-sans text-lg text-[#2C2C2C]/50 leading-relaxed">
            Not because life was hard. But because no one really knew them. 
            They had people around—but not someone <span className="italic">with</span> them.
          </p>
        </motion.div>

        {/* Chair Image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.4 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden mb-12">
            <img
              src="https://images.unsplash.com/photo-1620871267646-3ae55a411092?w=1600&q=80"
              alt="Two chairs side by side facing the sunset"
              className="w-full h-[50vh] md:h-[60vh] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/70 via-transparent to-transparent" />
            
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <p className="font-serif italic text-2xl md:text-4xl text-white/95 max-w-2xl leading-snug">
                "What if we just... pulled up a chair?"
              </p>
            </div>
          </div>

          {/* Closing */}
          <div className="max-w-2xl mx-auto text-center">
            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="font-serif italic text-xl md:text-2xl text-[#2C2C2C]/70"
            >
              So we pulled up a chair. No advice, no fixing—just alongside.
            </motion.p>
          </div>
        </motion.div>

      </div>
    </section>
  );
};
